/**
 * Sentry Error Monitoring Configuration
 * Initialized as early as possible in the app lifecycle.
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN
    || 'https://29c2ad09f379a506721d2390558ed349@o4510869219901440.ingest.us.sentry.io/4510869222719488';

/**
 * Initialize Sentry error monitoring.
 */
export const initSentry = () => {
    try {
        Sentry.init({
            dsn: SENTRY_DSN,
            environment: process.env.NODE_ENV || 'production',
            release: process.env.REACT_APP_VERSION || '1.0.0',
            sendDefaultPii: true,

            // Performance monitoring — sample 20% in prod, 100% in dev
            tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0,

            // Session replay — 10% of sessions, 100% on errors
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,

            // Filter out noisy errors
            ignoreErrors: [
                'top.GLOBALS',
                'originalCreateNotification',
                'canvas.contentDocument',
                'Network Error',
                'NetworkError',
                'Failed to fetch',
                'Load failed',
                'ResizeObserver loop',
                'AbortError',
                'CanceledError',
            ],

            // Sanitize sensitive data before sending
            beforeSend(event) {
                if (event.request?.url) {
                    try {
                        const url = new URL(event.request.url);
                        url.searchParams.delete('token');
                        url.searchParams.delete('password');
                        url.searchParams.delete('api_key');
                        event.request.url = url.toString();
                    } catch (e) {
                        // URL parse failed, send as-is
                    }
                }
                return event;
            },
        });

        if (process.env.NODE_ENV === 'development') {
            console.info('[Monitoring] ✅ Sentry initialized');
        }
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Monitoring] Sentry init failed:', error.message);
        }
    }
};

/**
 * Capture an error manually
 */
export const captureError = (error, context = {}) => {
    Sentry.captureException(error, { extra: context });
    console.error(error);
};

/**
 * Capture a message/event
 */
export const captureMessage = (message, level = 'info') => {
    Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setUser = (user) => {
    Sentry.setUser(user ? {
        id: user.id,
        email: user.email,
        username: user.name,
        role: user.role,
    } : null);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (category, message, data = {}) => {
    Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
    });
};

const monitoring = {
    initSentry,
    captureError,
    captureMessage,
    setUser,
    addBreadcrumb,
};

export default monitoring;
